<script setup lang="ts">
import { KeySquare } from 'lucide-vue-next'
import { inject, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { useRouter } from 'vue-router'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { DynamicModalType } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import FapFapError, { AlreadyProError, UpgradeFromBasicError } from '@/errors/FapFapError'
import { RequestAction, type RequestRouter } from '@/request/requestRouter'
import RedeemMessages from '@/components/subscription/RedeemMessages.vue'

const { t } = useI18n()

const serialKey = ref<string>('')
const errorCode = ref<string>('')
const tickets = ref<number>(0)
const duration = ref<number>(0)
const nospam = shallowRef<boolean>(false)
const router = useRouter()
const request = inject<RequestRouter>('request')

async function redeem() {
	nospam.value = true

	setTimeout(() => {
		nospam.value = false
	}, 500)
	resetMessage()
	if (!useAuthStore().isAuth()) {
		router.push('/account/signup')
	}
	else {
		const res = await request?.exec(RequestAction.RedeemG2A, {
			routeParams: {
				serialKey: serialKey.value,
			},
			body: {},
		})

		if (res) {
			const data = await res.json()
			if (!res.ok) {
				const error = FapFapError.auto(data.detail.code, data.detail?.data)
				if (error) {
					if (error instanceof UpgradeFromBasicError || error instanceof AlreadyProError) {
						useDynamicModalStore().showModal(DynamicModalType.RedeemConflicts, {
							props: {
								duration: error.data.duration,
								tickets: error.data.tickets,
								serialKey: serialKey.value,
								alreadyPro: error instanceof AlreadyProError,
							},
						})
					}
					else {
						errorCode.value = error.code
					}
				}
			}
			else {
				tickets.value = data.tickets
				if (typeof data.duration === 'number') {
					duration.value = data.duration
					useAuthStore().refreshUser()
				}
				else {
					duration.value = 0
				}

				useAuthStore().user.generation_token += tickets.value
			}
		}
	}
}

function resetMessage() {
	tickets.value = 0
	duration.value = 0
	errorCode.value = ''
}
</script>

<template>
	<div class="setting-card">
		<div class="setting-card-header">
			<div class="flex-center gap-2">
				<KeySquare class="lucide-button-icon" /> {{ t('dashboard.g2a.title') }}
			</div>
		</div>
		<div class="setting-card-content">
			<div class="flex items-center flex-wrap sm:justify-between justify-end gap-2 sm:gap-4">
				<InputText
					v-model="serialKey"
					class="grow flex-1 min-w-[200px] text-sm"
					:placeholder="t('dashboard.g2a.placeholder')"
				/>
				<Button
					class="button-orange-shade w-fit"
					:label="t('dashboard.g2a.submit')"
					:disabled="nospam || serialKey.length < 12"
					@click="redeem"
				/>
			</div>

			<RedeemMessages
				:error-code="errorCode"
				:tickets="tickets"
				:duration="duration"
				@close="resetMessage"
			/>
		</div>
	</div>
</template>

<style scoped lang="scss">

</style>
